import { organizationType, userType } from "../types"
import { BillingStatus, ChargeNowResult, SubscriptionSummary } from "../subscription"

export interface UserState {
    id: string
    email: string
    firstName?: string
    lastName?: string
    photoUrl?: string | null
    photoRevision: number
    applyPhoto: (userId: string, photoUrl: string | null) => void
    phone?: string
    sidebarRestoreLastVisited?: boolean
    sidebarShowRememberedShortcuts?: boolean
    machines: string[]
    locations: string[]
    isAdmin?: boolean
    isStaff?: boolean
    isContact?: boolean
    canViewCalendar?: boolean
    isSuperuser?: boolean
    isOperator?: boolean
    isOwner?: boolean
    viewMode?: "org" | "contact"
    accountMode: "lead_only" | "full" | null
    isAuthenticated: boolean
    initialized: boolean
    loading?: { on: boolean }

    securityChallenge: {
        action?: "change_email"
        challengeId?: string
        destinationHint?: string
        canResendAt?: number
    }

    stripeLoading: boolean
    stripeError: string | null
    financialSummary: {
        paymentMethodId?: string | null
        bankAccountId?: string | null
        paymentMethodLabel?: string | null
        bankAccountLabel?: string | null
        card?: {
            brand?: string | null
            last4?: string | null
            expMonth?: number | null
            expYear?: number | null
        } | null
        bank?: {
            bankName?: string | null
            last4?: string | null
            accountType?: string | null
        } | null
    } | null
    financialSummaryLoading: boolean
    financialSummaryError: string | null

    organization?: organizationType | null
    organizationLoading?: boolean
    organizationError?: string | null

    billingSummary: SubscriptionSummary | null
    billingStatus: BillingStatus | null
    subscriptionLoading: boolean
    subscriptionError: string | null
    subscriptionLastFetched: number | null
    authLastRefreshedAt: number | null
    chargeNowLoading: boolean
    chargeNowError: string | null
    chargeNowResult: ChargeNowResult | null
    
    init: () => Promise<void>
    refreshAuthIfStale: (options?: { force?: boolean; minAgeMs?: number }) => Promise<void>
    updateProfile: (payload: Partial<Pick<userType, "firstName" | "lastName" | "phone" | "sidebarRestoreLastVisited" | "sidebarShowRememberedShortcuts">>) => Promise<void>
    loadOrganization: () => Promise<void>
    updateOrganizationLogo: (file: File) => Promise<void>
    removeOrganizationLogo: () => Promise<void>
    setDefaultOrganizationLogo: () => Promise<void>
    updateOrganization: (payload: {
        title?: string
        timezone?: string
        notes?: string
        billingSendToAdmins?: boolean
        taskPlanEmailsEnabled?: boolean
        dailySummaryEmailsEnabled?: boolean
        weeklyMetricsEmailsEnabled?: boolean
        marketplaceOwnerMatchEmailsEnabled?: boolean
        marketplaceAdminMatchEmailsEnabled?: boolean
        warehouseTaskFlowEnabled?: boolean
        operatorTaskClaimingEnabled?: boolean
        serviceScheduleDays?: string[]
        defaultLocationLeadAssigneeId?: string | null
        applyGrowthTaskAssigneeTo?: import("../../vend/types").DefaultAssigneeApplyScope
        expectedGrowthSettingsRevision?: number
        defaultCommissionFundingAccountId?: string
        defaultServiceFrequency?: number
        maxSuggestedRouteDriveMinutes?: number
        maxSuggestedRouteDistanceMiles?: number
        maxSuggestedRouteDayLocations?: number
        maxSuggestedRouteDayMachines?: number
        locationCadenceChanges?: Array<{
            locationId: string
            expected: { frequency?: number | null; dayOfWeek?: string | null }
            next: { frequency: number; dayOfWeek: string }
        }>
    }) => Promise<void>
    getStripePaymentMethodSecret: () => Promise<string>
    getStripeBankAccountSecret: () => Promise<string>
    attachStripePaymentMethod: (paymentMethodId: string) => Promise<void>
    attachStripeBankAccount: (bankAccountId: string) => Promise<void>
    loadFinancialSummary: () => Promise<void>
    detachStripePaymentMethod: () => Promise<void>
    detachStripeBankAccount: () => Promise<void>
    refreshSubscription: (options?: { force?: boolean; minAgeMs?: number }) => Promise<void>
    chargeSubscriptionNow: () => Promise<ChargeNowResult>
    cancelSubscription: () => Promise<void>
    resumeSubscription: () => Promise<void>
    requestSecurityChallenge: (params: { action: "change_email"; currentPassword?: string; newEmail?: string; challengeId?: string }) => Promise<void>
    confirmSecurityChallenge: (params: { challengeId: string; code: string }) => Promise<{ status: string; email?: string }>
    clearSecurityChallenge: () => void

    setViewMode: (mode: "org" | "contact") => void
    signout: (options?: { redirectTo?: string }) => Promise<void>
    signin: (email: string, password: string) => Promise<boolean>
    signup: (user: Partial<userType>, password: string) => void
}

export const selector = (state: UserState) => ({
    id: state.id,
    email: state.email,
    firstName: state.firstName,
    lastName: state.lastName,
    photoUrl: state.photoUrl,
    photoRevision: state.photoRevision,
    applyPhoto: state.applyPhoto,
    phone: state.phone,
    sidebarRestoreLastVisited: state.sidebarRestoreLastVisited,
    sidebarShowRememberedShortcuts: state.sidebarShowRememberedShortcuts,
    machines: state.machines,
    locations: state.locations,
    isAdmin: state.isAdmin,
    isStaff: state.isStaff,
    isContact: state.isContact,
    canViewCalendar: state.canViewCalendar,
    isSuperuser: state.isSuperuser,
    isOperator: state.isOperator,
    isOwner: state.isOwner,
    viewMode: state.viewMode,
    accountMode: state.accountMode,
    isAuthenticated: state.isAuthenticated,
    initialized: state.initialized,
    securityChallenge: state.securityChallenge,
    stripeLoading: state.stripeLoading,
    stripeError: state.stripeError,
    financialSummary: state.financialSummary,
    financialSummaryLoading: state.financialSummaryLoading,
    financialSummaryError: state.financialSummaryError,
    organization: state.organization,
    organizationLoading: state.organizationLoading,
    organizationError: state.organizationError,
    billingSummary: state.billingSummary,
    billingStatus: state.billingStatus,
    subscriptionLoading: state.subscriptionLoading,
    subscriptionError: state.subscriptionError,
    subscriptionLastFetched: state.subscriptionLastFetched,
    authLastRefreshedAt: state.authLastRefreshedAt,
    chargeNowLoading: state.chargeNowLoading,
    chargeNowError: state.chargeNowError,
    chargeNowResult: state.chargeNowResult,

    init: state.init,
    refreshAuthIfStale: state.refreshAuthIfStale,
    updateProfile: state.updateProfile,
    loadOrganization: state.loadOrganization,
    updateOrganizationLogo: state.updateOrganizationLogo,
    removeOrganizationLogo: state.removeOrganizationLogo,
    setDefaultOrganizationLogo: state.setDefaultOrganizationLogo,
    updateOrganization: state.updateOrganization,
    getStripePaymentMethodSecret: state.getStripePaymentMethodSecret,
    getStripeBankAccountSecret: state.getStripeBankAccountSecret,
    attachStripePaymentMethod: state.attachStripePaymentMethod,
    attachStripeBankAccount: state.attachStripeBankAccount,
    loadFinancialSummary: state.loadFinancialSummary,
    detachStripePaymentMethod: state.detachStripePaymentMethod,
    detachStripeBankAccount: state.detachStripeBankAccount,
    refreshSubscription: state.refreshSubscription,
    chargeSubscriptionNow: state.chargeSubscriptionNow,
    cancelSubscription: state.cancelSubscription,
    resumeSubscription: state.resumeSubscription,
    requestSecurityChallenge: state.requestSecurityChallenge,
    confirmSecurityChallenge: state.confirmSecurityChallenge,
    clearSecurityChallenge: state.clearSecurityChallenge,

    setViewMode: state.setViewMode,
    signin: state.signin,
    signup: state.signup,
    signout: state.signout,
})
